import { Rgb, srgbChannelToLinear } from './color-space.utils';

/**
 * WCAG 2.x relative luminance and contrast.
 *
 * WCAG rather than APCA on purpose: the goal is to reproduce, in dark mode,
 * the contrast a backend-authored colour already had against white. WCAG is
 * the ratio the light theme was implicitly designed against, and it is
 * symmetric, which lets the dark-mode target be solved in closed form.
 */

/** 0–1. */
export function relativeLuminance(rgb: Rgb): number {
  const red = srgbChannelToLinear(rgb.r / 255);
  const green = srgbChannelToLinear(rgb.g / 255);
  const blue = srgbChannelToLinear(rgb.b / 255);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

/** 1–21, order-independent. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  return contrastRatioFromLuminance(relativeLuminance(a), relativeLuminance(b));
}

export function contrastRatioFromLuminance(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * The luminance a colour must reach to hit `ratio` against a background of
 * luminance `backgroundLuminance`, assuming it ends up the LIGHTER of the two.
 *
 * Closed form, which is what removes an outer loop from the adaptation solve:
 * only the (non-invertible) map from OKLCH lightness to luminance still needs
 * a numeric search.
 */
export function targetLuminanceFor(
  ratio: number,
  backgroundLuminance: number,
): number {
  return ratio * (backgroundLuminance + 0.05) - 0.05;
}
